import React from 'react'
import { checkProviders, decodeJwtEmail, isApiError, signInWithMicrosoft, tokenStore } from '../api'
import { completeLogin } from '../utils/complete-login'
import { SSO_LOGIN_DISABLED_MESSAGE } from '../utils/login-method-messages'
import {
  MICROSOFT_CLIENT_ID,
  isMicrosoftCancellation,
  signInWithMicrosoftPopup,
} from '../utils/microsoft-msal'
import { Button } from './Button'
import { useToast } from './Toast'

export function MicrosoftLogo() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1.25 1.25H9.375V9.375H1.25V1.25Z" fill="#F35325" />
      <path d="M10.625 1.25H18.75V9.375H10.625V1.25Z" fill="#81BC06" />
      <path d="M1.25 10.625H9.375V18.75H1.25V10.625Z" fill="#05A6F0" />
      <path d="M10.625 10.625H18.75V18.75H10.625V10.625Z" fill="#FFBA08" />
    </svg>
  )
}

/**
 * "Continue with Microsoft" — drives the MSAL popup sign-in flow.
 *
 * Microsoft verification is itself a complete login, mirroring
 * {@link GoogleSignInButton}: before signing in it reads the email from the
 * Microsoft ID token and checks the account-wide login methods with
 * `POST /auth/check-providers` (blocking when the tenant admin has disabled
 * SSO, so we never hit `/auth/microsoft`), then POSTs the ID token to
 * `/auth/microsoft` and stores the returned session with a success toast.
 * Closing the popup is treated as a silent cancel; other failures are reported
 * via `onError`.
 *
 * When `VITE_MICROSOFT_CLIENT_ID` is unset the button renders disabled.
 */
export function MicrosoftSignInButton({
  onError,
}: {
  /** Called with the failure message, or `null` to clear it. */
  onError: (message: string | null) => void
}) {
  const { showToast } = useToast()
  const [pending, setPending] = React.useState(false)

  const handleClick = React.useCallback(async () => {
    setPending(true)
    onError(null)
    try {
      const idToken = await signInWithMicrosoftPopup()

      // Respect the account-wide login-method setting: read the email from the
      // Microsoft ID token and block when the tenant admin has disabled SSO, so
      // we never hit /auth/microsoft. Enforcement is UI-side — the sign-in
      // endpoint doesn't reject a disabled method.
      const email = decodeJwtEmail(idToken)
      if (email) {
        const { loginMethods } = await checkProviders(email)
        if (!loginMethods.ssoLoginEnabled) {
          onError(SSO_LOGIN_DISABLED_MESSAGE)
          setPending(false)
          return
        }
      }

      // SSO verification is a complete login — store the session and finish.
      const result = await signInWithMicrosoft(idToken)
      if (result.token) {
        tokenStore.setAccessToken(result.token)
        showToast('Login success', 'success')
        void completeLogin()
      }
      setPending(false)
    } catch (err) {
      // The user closing the popup isn't an error worth surfacing.
      if (isMicrosoftCancellation(err)) {
        setPending(false)
        return
      }
      onError(isApiError(err) ? err.message : 'Microsoft sign-in failed. Please try again.')
      setPending(false)
    }
  }, [onError, showToast])

  return (
    <Button
      type="button"
      id="btn-sso-microsoft"
      variant="outline"
      size="lg"
      onClick={handleClick}
      disabled={!MICROSOFT_CLIENT_ID || pending}
    >
      <MicrosoftLogo />
      {pending ? 'Signing in…' : 'Continue with Microsoft'}
    </Button>
  )
}
